import { noteIdFromResourceRef } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconSparkles } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Logo } from '#src/components/core/Logo.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useIsFloatingAssistantVisible } from '#src/hooks/useIsFloatingAssistantVisible.ts'

export function FloatingAssistantButton(): JSX.Element | null {
  const visible = useIsFloatingAssistantVisible()
  const setOpen = useAIAssistantStore((s) => s.setOpen)
  const resourceRef = useAIAssistantStore((s) => s.resourceRef)
  const setInitialPrompt = useAIAssistantStore((s) => s.setInitialPrompt)

  if (!visible) {
    return null
  }

  // "Extract notes from this visit note" is note-only — show it for a Composition
  // note, not for other edited resources (e.g. a CarePlan) that also stage edits.
  const noteId = noteIdFromResourceRef(resourceRef ?? undefined)

  return (
    // `print:hidden`: Orbit AI chrome (the "Extract to chart" action + the
    // floating assistant button) must never appear in a printed clinical
    // document — only the print header + note content should.
    <div className="absolute right-4 bottom-4 z-20 flex flex-col items-end gap-2 print:hidden">
      {noteId && (
        // shadcn's Suggestion is a Button with the prompt as its label:
        // https://ui.shadcn.com/docs/ai-elements/radix/suggestion
        <Button
          variant="outline"
          size="sm"
          className="bg-white shadow-lg"
          onClick={() => {
            setInitialPrompt('Extract notes from this visit note')
            setOpen(true)
          }}
        >
          <IconSparkles className="size-3.5" />
          Extract to chart
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Open Orbit AI"
            onClick={() => setOpen(true)}
            className="size-10 rounded-full bg-white shadow-lg"
          >
            <Logo size={26} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">Orbit AI</TooltipContent>
      </Tooltip>
    </div>
  )
}
